/**
 * Каталог библиотеки материалов: единственный источник истины.
 *
 * Спека: Obsidian `02-PROJECTS/english-kids/products/library-spec.md` (решение
 * 18.09.2026). Всё, что видно о материале на /biblioteka, /biblioteka/[slug],
 * в кабинете родителя, в карте сайта и в download-обработчиках, читается
 * отсюда. Название, класс и доступ нигде больше руками не дублировать.
 *
 * Добавить материал:
 *   1. Исходник первой страницы в `content/library/source/<slug>.jpg`.
 *   2. Запись ниже (`published: false`, пока файл не проверен глазами).
 *   3. `npx tsx scripts/library/build-library-assets.ts` соберёт превью
 *      `public/images/library/<slug>.webp` и брендированный PDF
 *      `content/library/files/<slug>.pdf` (QR ведёт на страницу материала).
 *   4. Проверить PDF, поставить `published: true`.
 *
 * Материал, который пришёл уже свёрстанным и брендированным (например, на
 * нескольких страницах: объяснение, задания, ответы), через этот скрипт НЕ
 * прогонять: он соберёт лист из одной картинки и потеряет всё остальное.
 * Такой файл кладётся в `content/library/files/` как есть, исходник первой
 * страницы снимается растром (`pdftoppm -jpeg -scale-to-x 900 -f 1 -l 1
 * -singlefile`), а превью собирается тем же скриптом со slug'ом и флагом
 * `--preview-only`. Без slug'а скрипт пересоберёт PDF всех материалов и
 * испортит многостраничные. Заголовок документа и кликабельные кнопки в таком
 * файле правит `scripts/library/patch-branded-pdf.ts`.
 *
 * Правила каталога (спека §8): только реально готовые файлы, никаких карточек
 * «скоро» и выдуманных количеств. Число материалов в тексте всегда считается
 * из этого массива.
 *
 * Доступ (спека §4.2–4.3):
 *   public  — скачивается без регистрации (лид-магниты из рекламы и соцсетей);
 *   account — нужен аккаунт семьи. Окончание триала библиотеку НЕ закрывает.
 */

export type LibraryGrade = 2 | 3 | 4;
export type LibraryAccess = 'public' | 'account';

export const LIBRARY_GRADES: readonly LibraryGrade[] = [2, 3, 4];

export type LibraryMaterial = {
  /** Адрес страницы: /biblioteka/<slug>. Латиница, класс в конце. */
  slug: string;
  /** Короткое имя для карточек и хлебных крошек. */
  shortTitle: string;
  /** H1 страницы материала. */
  title: string;
  grade: LibraryGrade;
  /** Раздел для подписи на карточке: «Лексика», «Грамматика». */
  topic: string;
  /** Одна строка для карточки каталога. */
  description: string;
  /** Абзац под H1: для кого и зачем. */
  intro: string;
  access: LibraryAccess;
  /** Имя PDF в `content/library/files/` (вне /public, см. спеку §6). */
  file: string;
  preview: {
    /** Путь в /public: первая страница, webp. */
    src: string;
    width: number;
    height: number;
    alt: string;
  };
  pageCount: number;
  format: 'PDF, A4';
  published: boolean;
  featured: boolean;
  seoTitle: string;
  seoDescription: string;
  /** Что внутри: группы слов так, как они сгруппированы в самом файле. */
  inside: { heading: string; items: string[] }[];
  /** 1–3 коротких совета родителю. */
  howToUse: string[];
  /** ISO-дата последнего обновления файла (для карты сайта). */
  updatedAt: string;
  relatedMaterialSlugs?: string[];
};

const MATERIALS: LibraryMaterial[] = [
  {
    slug: 'eda-2-klass',
    shortTitle: 'Еда',
    title: 'Еда: слова по теме для 2 класса',
    grade: 2,
    topic: 'Лексика',
    description: 'Фрукты, напитки, сладости и как сказать «я люблю».',
    intro:
      'Слова по теме «Еда», которые проходят во 2 классе: фрукты, напитки, блюда, сладости и фразы, чтобы сказать, что нравится и что нет. Все слова с картинкой и переводом на одной странице.',
    access: 'public',
    file: 'eda-2-klass.pdf',
    preview: {
      src: '/images/library/eda-2-klass.webp',
      width: 590,
      height: 971,
      alt: 'Первая страница материала «Еда»: слова с картинками и переводом',
    },
    pageCount: 1,
    format: 'PDF, A4',
    published: true,
    featured: true,
    seoTitle: 'Слова по теме «Еда» на английском для 2 класса: таблица с картинками',
    seoDescription:
      'Английские слова по теме «Еда» для 2 класса: фрукты, напитки, блюда, сладости, I like и I don’t like. Таблица с картинками и переводом, скачать PDF бесплатно.',
    inside: [
      { heading: 'Фрукты', items: ['apple', 'banana', 'orange'] },
      { heading: 'Напитки и молочные продукты', items: ['juice', 'milk'] },
      { heading: 'Еда и блюда', items: ['burger', 'sandwich', 'pizza', 'chips'] },
      { heading: 'Сладости и десерты', items: ['chocolate', 'ice cream', 'cake', 'biscuit'] },
      { heading: 'Вкус и предпочтения', items: ['yummy', 'yucky', 'I like', 'I don’t like'] },
      { heading: 'Дополнительные слова', items: ['food', 'favourite'] },
    ],
    howToUse: [
      'Распечатайте и повесьте на кухне. Слова рядом с едой запоминаются быстрее.',
      'За завтраком спросите: What do you like? Пусть ребёнок ответит фразой I like… с одним словом из таблицы.',
      'Через неделю закройте перевод ладонью и проверьте, какие слова ребёнок называет сам.',
    ],
    updatedAt: '2026-09-18',
    relatedMaterialSlugs: ['mestoimeniya-2-klass', 'my-home-3-klass'],
  },
  {
    slug: 'mestoimeniya-2-klass',
    shortTitle: 'Местоимения',
    title: 'Местоимения: таблица для 2 класса',
    grade: 2,
    topic: 'Грамматика',
    description: 'Я, ты, он, она и мой, твой, его: таблица для повторения.',
    intro:
      'Все местоимения, которые встречаются во 2 классе, в одной таблице: кто (I, he, she), чей (my, his, her) и кому (me, him, her). Русское слово слева, английское рядом, чтобы ребёнок видел пары.',
    access: 'account',
    file: 'mestoimeniya-2-klass.pdf',
    preview: {
      src: '/images/library/mestoimeniya-2-klass.webp',
      width: 853,
      height: 1280,
      alt: 'Первая страница материала «Местоимения»: русские местоимения и английские пары',
    },
    pageCount: 1,
    format: 'PDF, A4',
    published: true,
    featured: false,
    seoTitle: 'Местоимения в английском для 2 класса: таблица I, my, me',
    seoDescription:
      'Таблица местоимений английского языка для 2 класса: I, you, he, she, it, we, they, притяжательные my, your, his, her и формы me, him, us, them.',
    inside: [
      { heading: 'Кто', items: ['I', 'you', 'he', 'she', 'it', 'we', 'they'] },
      { heading: 'Чей', items: ['my', 'your', 'his', 'her', 'our', 'their'] },
      { heading: 'Кому, кого', items: ['me', 'him', 'her', 'us', 'them'] },
    ],
    howToUse: [
      'Начните с левого столбца: я, ты, он, она. Пусть ребёнок называет английскую пару, глядя на русское слово.',
      'Потом добавьте второй столбец парами: I и my, he и his. Так легче не путать he и his.',
      'Играйте в «покажи»: вы говорите his book, ребёнок показывает на книгу папы.',
    ],
    updatedAt: '2026-09-18',
    relatedMaterialSlugs: ['to-be-2-klass', 'eda-2-klass', 'my-home-3-klass'],
  },
  {
    slug: 'to-be-2-klass',
    shortTitle: 'Глагол to be',
    title: 'To be: am, is, are для 2 класса',
    grade: 2,
    topic: 'Грамматика',
    description: 'Когда am, когда is, когда are. С заданиями и ответами.',
    intro:
      'Правило am, is, are для 2 класса: с кем какое слово и почему в русском языке мы его просто не говорим. На первой странице объяснение с примерами, на второй четыре задания, на третьей ответы для родителя.',
    access: 'account',
    file: 'to-be-2-klass.pdf',
    preview: {
      src: '/images/library/to-be-2-klass.webp',
      width: 900,
      height: 1274,
      alt: 'Первая страница материала «To be»: правило am, is, are и таблица с примерами',
    },
    pageCount: 3,
    format: 'PDF, A4',
    published: true,
    featured: false,
    seoTitle: 'Глагол to be для 2 класса: таблица am, is, are с заданиями',
    seoDescription:
      'Глагол to be в английском для 2 класса: когда am, когда is, когда are. Таблица с примерами, четыре задания на отработку и ответы для родителей.',
    inside: [
      { heading: 'Глагол-связка', items: ['am', 'is', 'are'] },
      { heading: 'С кем am', items: ['I'] },
      { heading: 'С кем is', items: ['he', 'she', 'it'] },
      { heading: 'С кем are', items: ['you', 'we', 'they'] },
    ],
    howToUse: [
      'Прочитайте первую страницу вместе и остановитесь на строчке «Запомни». Три правила там уже собраны, учить таблицу целиком не нужно.',
      'Задания со второй страницы дайте на следующий день, а не сразу. Так видно, что осталось в голове, а не то, что ребёнок списал со строчки рядом.',
      'Проверяйте по третьей странице сами. Если одна и та же строчка не сходится с ответом, например про he и is, вернитесь к ней в таблице на первой странице.',
    ],
    updatedAt: '2026-09-20',
    relatedMaterialSlugs: ['mestoimeniya-2-klass', 'eda-2-klass'],
  },
  {
    slug: 'my-home-3-klass',
    shortTitle: 'My House',
    title: 'My House: слова по теме «Дом» для 3 класса',
    grade: 3,
    topic: 'Лексика',
    description: 'Слова по теме «Дом»: комнаты, мебель и предметы.',
    intro:
      'Комнаты, части дома, мебель и предметы: 32 слова с картинкой и переводом, сгруппированные так, как тему проходят в 3 классе. Все слова темы видны сразу, и повторять можно по одной группе.',
    access: 'public',
    file: 'my-home-3-klass.pdf',
    preview: {
      src: '/images/library/my-home-3-klass.webp',
      width: 720,
      height: 1280,
      alt: 'Первая страница материала My House: комнаты, мебель и предметы дома с картинками',
    },
    pageCount: 1,
    format: 'PDF, A4',
    published: true,
    featured: true,
    seoTitle: 'Слова по теме «Дом» на английском для 3 класса: My House с картинками',
    seoDescription:
      'Английские слова по теме «Мой дом» для 3 класса: комнаты, мебель, предметы, кухня и ванная. 32 слова с картинками и переводом, скачать PDF бесплатно.',
    inside: [
      {
        heading: 'Дом и комнаты',
        items: [
          'house',
          'tree house',
          'room',
          'kitchen',
          'bathroom',
          'living room',
          'bedroom',
          'garden',
        ],
      },
      { heading: 'Части дома', items: ['door', 'window', 'wall', 'floor'] },
      {
        heading: 'Мебель',
        items: ['chair', 'table', 'bed', 'armchair', 'sofa', 'desk', 'cupboard', 'wardrobe', 'shelf'],
      },
      {
        heading: 'Предметы дома',
        items: ['lamp', 'clock', 'mirror', 'radio', 'television', 'computer'],
      },
      { heading: 'Кухня', items: ['fridge', 'cooker', 'glass', 'plate'] },
      { heading: 'Ванная', items: ['bath'] },
    ],
    howToUse: [
      'Распечатайте и повесьте на видное место. Первые дни только смотрите и читайте вслух по одной группе.',
      'Закройте перевод и попросите назвать предметы в своей комнате: пять слов за раз, не больше.',
      'Через неделю вернитесь к списку и отметьте, что ребёнок называет сам. Остальное стоит повторить.',
    ],
    updatedAt: '2026-09-18',
    relatedMaterialSlugs: ['eda-2-klass', 'mestoimeniya-2-klass'],
  },
];

/** Все опубликованные материалы в порядке: класс, затем порядок в массиве. */
export function getPublishedMaterials(): LibraryMaterial[] {
  return MATERIALS.filter((m) => m.published).sort((a, b) => a.grade - b.grade);
}

/** Материал по slug, только опубликованный. Неопубликованный = 404. */
export function getMaterial(slug: string): LibraryMaterial | null {
  return MATERIALS.find((m) => m.slug === slug && m.published) ?? null;
}

/** Классы, для которых реально есть материалы: вкладки фильтра строятся из них. */
export function getAvailableGrades(): LibraryGrade[] {
  const present = new Set(getPublishedMaterials().map((m) => m.grade));
  return LIBRARY_GRADES.filter((g) => present.has(g));
}

export function countByGrade(grade: LibraryGrade): number {
  return getPublishedMaterials().filter((m) => m.grade === grade).length;
}

/**
 * Связанные материалы: сначала явно указанные, затем тот же класс, затем
 * остальные. Без самого материала, не больше `limit`.
 */
export function getRelatedMaterials(material: LibraryMaterial, limit = 3): LibraryMaterial[] {
  const all = getPublishedMaterials().filter((m) => m.slug !== material.slug);
  const explicit = (material.relatedMaterialSlugs ?? [])
    .map((s) => all.find((m) => m.slug === s))
    .filter((m): m is LibraryMaterial => Boolean(m));
  const rest = all
    .filter((m) => !explicit.includes(m))
    .sort((a, b) => Number(b.grade === material.grade) - Number(a.grade === material.grade));
  return [...explicit, ...rest].slice(0, limit);
}

/** Класс из query `?grade=3`: только существующие значения, иначе null. */
export function parseGrade(raw: string | string[] | undefined | null): LibraryGrade | null {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const n = Number(value);
  return (LIBRARY_GRADES as readonly number[]).includes(n) ? (n as LibraryGrade) : null;
}

/** Slug из query `?material=`: только существующий опубликованный. */
export function parseMaterialSlug(raw: string | string[] | undefined | null): string | null {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (!value || !/^[a-z0-9-]{1,80}$/.test(value)) return null;
  return getMaterial(value) ? value : null;
}

// --- Подписи -----------------------------------------------------------------

function ruPlural(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return one;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return few;
  return many;
}

export function pagesLabel(n: number): string {
  return `${n} ${ruPlural(n, 'страница', 'страницы', 'страниц')}`;
}

export function materialsCountLabel(n: number): string {
  return `${n} ${ruPlural(n, 'материал', 'материала', 'материалов')}`;
}

export function wordsCountLabel(n: number): string {
  return `${n} ${ruPlural(n, 'слово', 'слова', 'слов')}`;
}

export function gradeLabel(grade: LibraryGrade): string {
  return `${grade} класс`;
}

/** «для 3 класса» */
export function gradeGenitive(grade: LibraryGrade): string {
  return `для ${grade} класса`;
}
